import { memo, useMemo } from 'react';

import { FilePenIcon } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/shadcn/dialog';

import type {
  DataEditorConfig,
  DataEditorData
} from '@/components/ui/dataEditor/dataEditor.type';
import { KeyValue } from '@/components/ui/keyValue/keyValue';
import Table from '@/components/ui/table';
import type { TableUpdate } from '@/components/ui/table/table.type';
import { formatNumber, setAtPath } from '@/utils';

type Props = {
  dataEditor: DataEditorConfig;
  data: DataEditorData;
};

// data editor reader component
export const DataEditorReader = memo(({ dataEditor, data }: Props) => {
  const {
    modifiedDataKey = 'changes',
    initialDataKey = 'rows',
    modifiedDataTitle = 'Edited Data',
    table: tableConfig
  } = dataEditor;

  const { [modifiedDataKey]: modifiedData, [initialDataKey]: initialData } =
    data;

  // if initial data has not been saved with the form just display empty initial data with the correct indexing
  const dataSelectorPath = tableConfig.dataSelectorPath || 'rowId';
  const tableData = useMemo(
    () =>
      initialData ||
      Object.keys(modifiedData).map(key => {
        const rowData = {};
        setAtPath(rowData, dataSelectorPath, key);
        return rowData;
      }),
    [initialData, modifiedData, dataSelectorPath]
  );

  const tableUpdate: TableUpdate = useMemo(
    () => ({
      edit: {
        modifiedData,
        allowDisplayDiffs: true
      }
    }),
    [modifiedData]
  );

  const nbChange = tableData.length;
  const nbChangesMsg = `${formatNumber(
    tableData.length
  )} row${nbChange !== 1 ? 's have' : ' has'} been modified`;

  return (
    <KeyValue dataCyName="data-editor" title={modifiedDataTitle}>
      <Dialog>
        <DialogTrigger asChild>
          <div
            className="my-2 flex w-full cursor-pointer flex-row flex-nowrap items-center gap-4 rounded-md border border-border bg-accent px-4 py-2 text-sm hover:brightness-98 dark:hover:brightness-110"
            data-cy="data-editor-view-changes"
          >
            <FilePenIcon className="text-accent-foreground" size={22} />
            <section>
              <div>View edited table</div>
              <div
                className="text-muted-foreground text-xs"
                data-cy="data-editor-nb-changes"
              >
                {nbChangesMsg}
              </div>
            </section>
          </div>
        </DialogTrigger>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-none">
          <DialogHeader>
            <DialogTitle>
              <div className="flex flex-row flex-nowrap items-center gap-4 text-lg">
                <div>{modifiedDataTitle}</div>
                <div className="font-normal text-muted-foreground text-xs">
                  {nbChangesMsg}
                </div>
              </div>
            </DialogTitle>
          </DialogHeader>

          <div className="overflow-y-auto text-[13px] text-foreground leading-[1.25]">
            <div className="size-full overflow-auto">
              <div className="flex h-[80vh] w-full flex-col flex-nowrap items-center">
                <Table
                  config={tableConfig}
                  data={tableData}
                  update={tableUpdate}
                />
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </KeyValue>
  );
});
